import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { formatCents, formatDate, formatDay } from "@/lib/format";
import { BrandMark } from "@/components/BrandMark";
import { useToast } from "@/components/Toaster";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input, Label } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import type { Product } from "./catalog";
import { SUB_STATUS, every, type Subscription } from "./SubscriptionsPage";
import { useContactNames } from "./useContactNames";

/** One subscription: what it charges and when, its card, open invoices and access; switch plan or cancel. */
export function SubscriptionDetailPage() {
  const { subscriptionId } = useParams({ from: "/billing/subscriptions/$subscriptionId" });
  const id = Number(subscriptionId);
  const q = useQuery({ queryKey: ["subscription", id], queryFn: () => api<Subscription>(`/api/v1/subscriptions/${id}`) });
  const name = useContactNames(q.data ? [q.data.contact_id] : []);
  const [dialog, setDialog] = useState<"switch" | "cancel" | null>(null);
  if (q.isLoading) return <Skeleton className="h-96" />;
  if (!q.data) return <p className="text-sm text-[var(--color-danger-fg)]">{q.error?.message ?? "Subscription not found."}</p>;
  const s = q.data;
  const st = SUB_STATUS[s.status] ?? { label: s.status, variant: "muted" as const };
  const over = s.status === "cancelled" || s.status === "ended";
  return (
    <div className="mx-auto max-w-[1000px] space-y-3">
      <Link to="/billing/subscriptions" className="inline-flex items-center gap-1 text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]">
        <ArrowLeft className="size-4" /> Subscriptions
      </Link>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold">{s.product_name}</h1>
        <BrandMark id={s.brand_id} />
        <Badge variant={st.variant}>{st.label}</Badge>
        {!s.billing_enabled && <Badge variant="muted">charged in Keap</Badge>}
        {s.auto_charge === false && <Badge variant="outline">manual pay</Badge>}
        <span className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" disabled={over} onClick={() => setDialog("switch")}>
            Switch plan
          </Button>
          <Button size="sm" variant="outline" disabled={over || !!s.cancel_at} onClick={() => setDialog("cancel")} title={s.cancel_at ? `Already ends ${formatDay(s.cancel_at)}` : undefined}>
            Cancel
          </Button>
        </span>
      </div>
      <p className="text-sm">
        <Link to="/contacts/$contactId" params={{ contactId: String(s.contact_id) }} className="font-medium text-[var(--color-primary)] hover:underline">
          {name(s.contact_id)}
        </Link>
        <span className="text-[var(--color-muted-foreground)]">: {s.summary}</span>
      </p>
      <div className="grid gap-3 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Billing</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-sm">
              <Row k="Amount">
                {formatCents(s.amount_cents)} {every(s)}
              </Row>
              {s.pending_price_id && <Row k="Plan change">switches at the next renewal</Row>}
              <Row k="Next charge">{s.next_bill_date ? formatDay(s.next_bill_date) : "none"}</Row>
              {s.current_period_end && (
                <Row k="Paid through">
                  {formatDay(s.current_period_end)}
                </Row>
              )}
              {s.trial_ends_on && <Row k="Trial ends">{formatDay(s.trial_ends_on)}</Row>}
              {s.cancel_at && <Row k="Ends">{formatDay(s.cancel_at)} (cancelled at period end)</Row>}
              {s.cancelled_at && <Row k="Cancelled">{formatDate(s.cancelled_at)}{s.cancel_reason ? `: ${s.cancel_reason}` : ""}</Row>}
              {s.past_due_since && (
                <Row k="Past due since">
                  <span className="text-[var(--color-danger-fg)]">{formatDay(s.past_due_since)}</span>
                </Row>
              )}
              {s.access_suspended_at && <Row k="Access paused">{formatDate(s.access_suspended_at)} (comes back when they pay)</Row>}
              <Row k="Renewals">{s.auto_charge === false ? "manual pay: an invoice and a pay link by email, no card charged" : "charged to the card"}</Row>
              <Row k="Card">{s.card || (s.payment_method_id ? "saved card" : "their default card (none linked to this subscription)")}</Row>
              <Row k="Billed by">{s.billing_enabled ? "the CRM" : "Keap, until the go-live switch"}</Row>
              <Row k="Started">
                {formatDate(s.started_at)}
                {s.source === "keap_import" ? " (in Keap)" : ""}
              </Row>
            </dl>
          </CardContent>
        </Card>
        <div className="space-y-3">
          <Card>
            <CardHeader>
              <CardTitle>Open invoices</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1.5 text-sm">
              {s.open_invoices.length === 0 && <p className="text-[var(--color-muted-foreground)]">None.</p>}
              {s.open_invoices.map((iv) => (
                <div key={iv.id} className="flex items-baseline justify-between gap-3">
                  <span>
                    <span className="num">{iv.number}</span>
                    <span className="block text-xs text-[var(--color-muted-foreground)]">
                      {iv.description ?? ""} {iv.due_date ? `due ${formatDay(iv.due_date)}` : ""}
                    </span>
                  </span>
                  <span className="num">{formatCents(iv.amount_due_cents - iv.amount_paid_cents)}</span>
                </div>
              ))}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Access it gives</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1 text-sm">
              {s.access.length === 0 && <p className="text-[var(--color-muted-foreground)]">None right now.</p>}
              {s.access.map((a) => (
                <div key={a.id} className="flex justify-between gap-3">
                  <span>{a.access_key.replace(/_/g, " ")}</span>
                  <span className="text-xs text-[var(--color-muted-foreground)]">{a.revoked_at ? "ended" : a.ends_at ? `until ${formatDate(a.ends_at)}` : "while it's paid"}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
      {dialog === "switch" && <SwitchPlanDialog s={s} onClose={() => setDialog(null)} />}
      {dialog === "cancel" && <CancelDialog s={s} who={name(s.contact_id)} onClose={() => setDialog(null)} />}
    </div>
  );
}

function Row({ k, children }: { k: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-[var(--color-muted-foreground)]">{k}</dt>
      <dd>{children}</dd>
    </>
  );
}

function useDone(id: number, done: string, onClose: () => void) {
  const qc = useQueryClient();
  const toast = useToast();
  return {
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["subscription", id] });
      void qc.invalidateQueries({ queryKey: ["subscriptions"] });
      toast(done);
      onClose();
    },
    onError: (e: Error) => toast(e.message, "error"),
  };
}

/** subscriptions_switch_plan: another recurring price of the same product, from the next renewal, no proration. The
 * Review step is the confirmation (the switch changes the next charge), so the save sends confirm. */
function SwitchPlanDialog({ s, onClose }: { s: Subscription; onClose: () => void }) {
  const product = useQuery({ queryKey: ["product", s.product_id], queryFn: () => api<Product>(`/api/v1/products/${s.product_id}`) });
  const options = (product.data?.prices ?? []).filter((p) => p.type === "recurring" && p.active && p.id !== s.price_id);
  const [priceId, setPriceId] = useState<number | null>(null);
  const [step, setStep] = useState<"pick" | "confirm">("pick");
  const chosen = options.find((p) => p.id === priceId);
  const sw = useMutation({ mutationFn: () => api(`/api/v1/subscriptions/${s.id}/switch`, { json: { price_id: priceId, confirm: true } }), ...useDone(s.id, "Plan switch saved", onClose) });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[min(520px,calc(100vw-2rem))]">
        <DialogTitle>Switch plan</DialogTitle>
        <DialogDescription>
          The new plan starts at the next renewal{s.next_bill_date ? ` (${formatDay(s.next_bill_date)})` : ""}. Nothing is charged or refunded now (no proration).
        </DialogDescription>
        {step === "pick" ? (
          <div className="space-y-1.5 text-sm" role="radiogroup" aria-label="New plan">
            {product.isLoading && <Skeleton className="h-12" />}
            {!product.isLoading && options.length === 0 && <p className="text-[var(--color-muted-foreground)]">This product has no other subscription price to switch to.</p>}
            {options.map((p) => (
              <label key={p.id} className="flex items-center gap-2 rounded-md border p-2">
                <input type="radio" name="sw-price" className="accent-[var(--color-primary)]" checked={priceId === p.id} onChange={() => setPriceId(p.id)} />
                <span className="font-medium">{p.summary}</span>
                <span className="text-xs text-[var(--color-muted-foreground)]">{p.name}</span>
              </label>
            ))}
          </div>
        ) : (
          <p className="rounded-md border p-3 text-sm">
            From {s.next_bill_date ? formatDay(s.next_bill_date) : "the next renewal"}: <span className="font-medium">{chosen?.summary}</span> instead of {formatCents(s.amount_cents)} {every(s)}. The member gets no email about it; tell them if they asked by phone.
          </p>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={step === "confirm" ? () => setStep("pick") : onClose}>
            {step === "confirm" ? "Back" : "Cancel"}
          </Button>
          {step === "pick" ? (
            <Button disabled={!chosen} onClick={() => setStep("confirm")}>
              Review
            </Button>
          ) : (
            <Button disabled={sw.isPending} onClick={() => sw.mutate()}>
              Switch plan
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** subscriptions_cancel: at period end (default) or now. Emails the member a confirmation. */
function CancelDialog({ s, who, onClose }: { s: Subscription; who: string; onClose: () => void }) {
  const [when, setWhen] = useState<"period_end" | "now">("period_end");
  const [reason, setReason] = useState("");
  const [step, setStep] = useState<"pick" | "confirm">("pick");
  const cancel = useMutation({
    mutationFn: () => api(`/api/v1/subscriptions/${s.id}/cancel`, { json: { when, reason: reason.trim() || undefined } }),
    ...useDone(s.id, when === "now" ? "Cancelled now" : "Cancelled at period end", onClose),
  });
  const paidThrough = s.current_period_end ?? s.next_bill_date;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[min(540px,calc(100vw-2rem))]">
        <DialogTitle>Cancel {who}'s {s.product_name}?</DialogTitle>
        {step === "pick" ? (
          <>
            <DialogDescription>Choose when it ends. Either way the member gets a confirmation email.</DialogDescription>
            <div className="space-y-2 text-sm" role="radiogroup" aria-label="When it ends">
              <label className="flex items-start gap-2 rounded-md border p-2">
                <input type="radio" name="cx-when" className="mt-1 accent-[var(--color-primary)]" checked={when === "period_end"} onChange={() => setWhen("period_end")} />
                <span>
                  <span className="font-medium">At the end of what they've paid for</span> (usual)
                  <span className="block text-xs text-[var(--color-muted-foreground)]">
                    No more charges; access continues{paidThrough ? ` until ${formatDay(paidThrough)}` : " until the paid time runs out"}.
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-2 rounded-md border p-2">
                <input type="radio" name="cx-when" className="mt-1 accent-[var(--color-primary)]" checked={when === "now"} onChange={() => setWhen("now")} />
                <span>
                  <span className="font-medium">Now</span>
                  <span className="block text-xs text-[var(--color-muted-foreground)]">Access ends today and unpaid invoices are voided. Nothing is refunded; refund a payment separately if needed.</span>
                </span>
              </label>
              <div className="space-y-1">
                <Label htmlFor="cx-reason">Reason (kept on the subscription)</Label>
                <Input id="cx-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Asked by phone, Oct 26" />
              </div>
            </div>
          </>
        ) : (
          <p className="rounded-md border p-3 text-sm">
            {when === "now" ? (
              <>
                Ends <span className="font-medium">today</span>: access stops now, unpaid invoices are voided, no refund.
              </>
            ) : (
              <>
                Ends <span className="font-medium">{paidThrough ? formatDay(paidThrough) : "at the end of the paid period"}</span>: no more charges, access until then.
              </>
            )}{" "}
            {who} gets a confirmation email.{reason.trim() ? ` Reason: ${reason.trim()}.` : ""}
          </p>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={step === "confirm" ? () => setStep("pick") : onClose}>
            {step === "confirm" ? "Back" : "Keep it"}
          </Button>
          {step === "pick" ? (
            <Button onClick={() => setStep("confirm")}>Review</Button>
          ) : (
            <Button variant="destructive" disabled={cancel.isPending} onClick={() => cancel.mutate()}>
              {when === "now" ? "Cancel now" : "Cancel at period end"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
